<template>
    <div class="automation-action-settings" style="min-width: 70%;">
        <div class="field">
            <label class="d-flex justify-content-between align-items-center">
                <strong>{{ t('automations-form-actions-calculator-formula_label') }}</strong>
                <kv-chip v-if="formula && !isError" color="success" size="small">
                    {{ t('automations-form-actions-calculator-syntax_valid') }}
                </kv-chip>
                <kv-chip v-else-if="formula && isError" color="error" size="small">
                    {{ t('automations-form-actions-calculator-syntax_error') }}
                </kv-chip>
            </label>

            <div class="formula-tools mb-2">
                <small class="text-muted">{{ t('automations-form-actions-calculator-insert') }}</small>
                <kv-tooltip
                    v-for="tool in formulaTools"
                    :key="tool.insert"
                    :text="t('automations-form-actions-calculator-' + tool.tooltip)"
                >
                    <kv-button variant="outlined" size="small" @click="insertMath(tool.insert)">
                        {{ tool.label }}
                    </kv-button>
                </kv-tooltip>
            </div>

            <kv-input
                ref="formulaInput"
                v-model="formula"
                class="formula-input"
                :placeholder="t('automations-form-actions-calculator-formula_placeholder')"
                :validator="formulaValidator"
                always-validate
                @input="handleInput"
            >
                <template #prepend>
                    <span class="formula-prefix text-monospace">f(x) =</span>
                </template>
            </kv-input>

            <small class="text-muted mt-1 d-block">
                {{ t('automations-form-actions-calculator-help_operators') }}
                <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>( )</code>. <br>
                {{ t('automations-form-actions-calculator-help_decimals') }} <br>
                {{ t('automations-form-actions-calculator-help_functions') }} <code>max(2 + 2, 3)</code>
            </small>
        </div>

        <hr class="mt-4 mb-4" />

        <div class="field mt-3">
            <label class="mb-3">
                {{ t('automations-form-actions-calculator-variables_label') }}
            </label>

            <div v-if="detectedVariables.length === 0" class="text-muted">
                <small>
                    <Icon icon-class="ui-help-circle" size="1" />
                    {{ t('automations-form-actions-calculator-variables_empty') }} <code>x * y</code>
                </small>
            </div>

            <div class="variables-list">
                <transition-group name="list" tag="div">
                    <kv-card v-for="varName in detectedVariables" :key="varName" density="compact" class="mb-3">
                        <template #text>
                            <div class="row align-items-center">
                                <div class="col-md-2 divider-right text-center">
                                    <kv-chip color="primary" class="text-monospace">{{ varName }}</kv-chip>
                                </div>

                                <div class="col-md-10">
                                    <div class="row">
                                        <div class="col-md-7">
                                            <kv-input
                                                v-model="varsConfig[varName].mapping"
                                                size="small"
                                                :label="t('automations-form-actions-calculator-mapping_label') + ' *'"
                                                :placeholder="t('automations-form-actions-calculator-mapping_placeholder', [varName])"
                                                :validator="checkMappingFormat"
                                                always-validate
                                                @input="changed"
                                            />
                                            <small v-if="!checkMappingFormat(varsConfig[varName].mapping)" class="mapping-error text-danger">
                                                {{ t('automations-form-actions-calculator-mapping_invalid') }}
                                                <code>[[objeto.chave]]</code>
                                            </small>
                                        </div>

                                        <div class="col-md-5">
                                            <kv-input
                                                v-model="varsConfig[varName].example"
                                                type="number"
                                                size="small"
                                                :label="t('automations-form-actions-calculator-example_label')"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </kv-card>
                </transition-group>
            </div>
        </div>

        <hr />

        <kv-card variant="soft" density="compact">
            <template #title>
                <span class="text-muted small">{{ t('automations-form-actions-calculator-preview_title') }}</span>
            </template>
            <template #text>
                <div class="row align-items-center">
                    <div class="col-md-7">
                        <div class="d-flex flex-column">
                            <small class="text-muted mb-1">{{ t('automations-form-actions-calculator-interpretation') }}</small>
                            <code class="simulation-value text-dark bg-white p-2 rounded border mb-0 text-truncate" :title="previewString">
                                {{ previewString || t('automations-form-actions-calculator-waiting_formula') }}
                            </code>
                        </div>
                    </div>
                    <div class="col-md-5 text-right divider-left">
                        <small class="text-muted d-block mb-1">{{ t('automations-form-actions-calculator-result_label') }}</small>
                        <div class="simulation-value simulation-result">
                            <h2 class="m-0 text-monospace" :class="isError ? 'text-danger' : 'text-success'">
                                {{ resultPreview }}
                            </h2>
                        </div>
                    </div>
                </div>

                <div v-if="isError" class="alert alert-danger mt-3 mb-0 py-2 small">
                    <Icon icon-class="ui-alert-triangle" size="1" />
                    {{ errorMessage }}
                </div>
            </template>
        </kv-card>
    </div>
</template>

<style scoped>
@media (min-width: 768px) {
    .divider-right {
        border-right: 1px solid #dee2e6;
    }
    .divider-left {
        border-left: 1px solid #dee2e6;
    }
}
.simulation-value {
    height: 40px;
}
code.simulation-value {
    display: block;
    line-height: 22px;
}
.simulation-result {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}
.simulation-result h2 {
    line-height: 1;
}
.formula-tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}
.automation-action-settings ::v-deep .kv-native-input {
    margin-top: 0;
}
.formula-input ::v-deep .kv-native-input,
.formula-input ::v-deep .kv-placeholder {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.automation-action-settings .field .mapping-error {
    margin-top: 4px;
    margin-bottom: 0;
}
.formula-prefix {
    padding-right: 8px;
    white-space: nowrap;
}
</style>

<script>
const LOCALES = { pt: "pt-BR", en: "en-US", es: "es-ES" };
const SAFE_EXPRESSION = /^(?:\s*(?:\d+(?:\.\d+)?(?![\d.])|(?:max|min|round|floor|ceil|abs)(?=\s*\()|[-+*/(),]))*\s*$/;
const COMMENT_START = /\/[/*]/;
const FUNCTION_CALL_END = /(?:max|min|round|floor|ceil|abs)\s*$/;

const withArity = (fn, min, max) => (...args) => {
    if (args.length < min || args.length > max) {
        throw new Error("Invalid number of arguments");
    }
    return fn(...args);
};

const phpRound = (value, precision = 0) => {
    const factor = Math.pow(10, Math.trunc(precision));
    const scaled = Number((Math.abs(value) * factor).toPrecision(15));
    return Math.sign(value) * Math.round(scaled) / factor;
};

const MATH_FUNCTIONS = {
    max: withArity(Math.max, 2, Infinity),
    min: withArity(Math.min, 2, Infinity),
    round: withArity(phpRound, 1, 2),
    floor: withArity(Math.floor, 1, 1),
    ceil: withArity(Math.ceil, 1, 1),
    abs: withArity(Math.abs, 1, 1),
};

const hasLooseComma = (expression) => {
    const parens = [];
    for (let i = 0; i < expression.length; i++) {
        const char = expression[i];
        if (char === "(") {
            parens.push(FUNCTION_CALL_END.test(expression.slice(0, i)));
        } else if (char === ")") {
            parens.pop();
        } else if (char === "," && !parens[parens.length - 1]) {
            return true;
        }
    }
    return false;
};

export default {
    props: {
        properties: Object,
        conditions: Array,
    },
    data: function () {
        return {
            title: this.t("automations-form-actions-calculator-title"),
            description: this.t("automations-form-actions-calculator-description"),
            formula: this.properties.formula || "",
            varsConfig: this.properties.varsConfig || {},
            detectedVariables: [],
            formulaTools: [
                { label: "max", insert: "max(", tooltip: "fn_max" },
                { label: "min", insert: "min(", tooltip: "fn_min" },
                { label: "round", insert: "round(", tooltip: "fn_round" },
                { label: "abs", insert: "abs(", tooltip: "fn_abs" },
                { label: "floor", insert: "floor(", tooltip: "fn_floor" },
                { label: "ceil", insert: "ceil(", tooltip: "fn_ceil" },
                { label: "(", insert: "(", tooltip: "open_parenthesis" },
                { label: ")", insert: ")", tooltip: "close_parenthesis" },
            ],
            mathFunctions: Object.keys(MATH_FUNCTIONS),
        };
    },
    mounted: function () {
        this.$emit("mounted", this);
        this.parseVariables();
    },
    computed: {
        previewString: function() {
            if (!this.formula) return "";
            let preview = this.formula;

            this.detectedVariables.forEach(v => {
                const val = this.varsConfig[v]?.example ?? 0;
                const regex = new RegExp(`\\b${v}\\b`, 'g');
                preview = preview.replace(regex, val);
            });
            return preview;
        },

        evaluation: function() {
            if (!this.formula || this.formula.trim() === '') {
                return { result: 0, errorKey: null };
            }

            try {
                let scopeValues = {};
                this.detectedVariables.forEach(v => {
                    let val = parseFloat(this.varsConfig[v]?.example);
                    if (isNaN(val)) val = 0;
                    scopeValues[v] = val;
                });

                const result = this.evaluateMath(this.formula, scopeValues);

                if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
                    return { result: null, errorKey: "error_invalid_result" };
                }

                return { result, errorKey: null };
            } catch (e) {
                return { result: null, errorKey: e.errorKey || "error_syntax" };
            }
        },

        isError: function() {
            return this.evaluation.errorKey !== null;
        },

        errorMessage: function() {
            if (!this.isError) return "";
            return this.t("automations-form-actions-calculator-" + this.evaluation.errorKey);
        },

        resultPreview: function() {
            if (this.isError) return this.t("automations-form-actions-calculator-error");

            const locale = LOCALES[this.$root.lang] || LOCALES.pt;
            return this.evaluation.result.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },

        handleInput() {
            this.parseVariables();
        },

        insertMath(text) {
            this.formula += text;
            this.parseVariables();
            this.$nextTick(() => {
                if (this.$refs.formulaInput) this.$refs.formulaInput.focus();
            });
        },

        parseVariables() {
            const regex = /[a-zA-Z_]\w*/g;
            const matches = this.formula.match(regex) || [];

            const uniqueVars = [...new Set(matches)].filter(v => {
                return !this.mathFunctions.includes(v) &&
                       !['true', 'false', 'null', 'undefined', 'NaN', 'Infinity'].includes(v);
            });

            this.detectedVariables = uniqueVars;

            uniqueVars.forEach(v => {
                if (!this.varsConfig[v]) {
                    this.$set(this.varsConfig, v, { mapping: '', example: 10 });
                }
            });

            this.changed();
        },

        evaluateMath(formulaString, userVariables) {
            const expression = Object.keys(userVariables).reduce(
                (acc, name) => acc.replace(new RegExp(`\\b${name}\\b`, "g"), `(${userVariables[name]})`),
                formulaString
            );

            if (!SAFE_EXPRESSION.test(expression) || COMMENT_START.test(expression)) {
                throw new Error("Unsafe expression");
            }

            if (hasLooseComma(expression)) {
                const error = new Error("Comma outside a function call");
                error.errorKey = "error_decimal_comma";
                throw error;
            }

            const mathKeys = this.mathFunctions;
            const mathValues = mathKeys.map(k => MATH_FUNCTIONS[k]);
            const dynamicFunc = new Function(...mathKeys, `"use strict"; return (${expression});`);

            return dynamicFunc(...mathValues);
        },

        formulaValidator() {
            return !this.isError;
        },

        checkMappingFormat(value) {
            if (!value) return false;
            const regex = /^\[\[[a-zA-Z0-9_\-]+\.[a-zA-Z0-9_\-]+\]\]$/;
            return regex.test(value);
        },

        isValid: function () {
            if (!this.formula || this.formula.trim().length === 0) return false;

            if (this.isError) return false;

            if (this.detectedVariables.length > 0) {
                const allMapped = this.detectedVariables.every(v => {
                    const config = this.varsConfig[v];
                    return config && this.checkMappingFormat(config.mapping);
                });
                if (!allMapped) return false;
            }

            return true;
        },

        changed: function () {
            const varsConfig = this.detectedVariables.reduce((acc, name) => {
                if (this.varsConfig[name]) acc[name] = this.varsConfig[name];
                return acc;
            }, {});

            this.$emit("propertiesUpdated", {
                formula: this.formula,
                varsConfig
            });
        },
    },
    watch: {
        isError: function () {
            this.$nextTick(() => {
                if (this.$refs.formulaInput) this.$refs.formulaInput.validate();
            });
        },
        varsConfig: {
            handler: function() { this.changed(); },
            deep: true
        }
    },
};
</script>
